import React, { useEffect, useState } from "react";
import axios from "axios";

import { startOfMonth } from "date-fns";
import ArrowUp from "@material-ui/icons/ArrowUpward";
import ArrowDown from "@material-ui/icons/ArrowDownward";
import Whatshot from "@material-ui/icons/Whatshot";

import { Box, Tabs, Tab } from "@material-ui/core";
import { CompactTable } from "@table-library/react-table-library/compact";

import {
  DEFAULT_OPTIONS,
  getTheme,
} from "@table-library/react-table-library/material-ui";
import { useTheme } from "@table-library/react-table-library/theme";

const users = [
  {
    name: "Simon",
    lichess: "maddremory",
    chesscom: "drabmemory",
  },
  {
    name: "Fred",
    lichess: "faridaghabayov",
    chesscom: "blundermequeens",
  },
  {
    name: "Eamonn",
    lichess: "kinglazy",
  },
  {
    name: "Aziz (GV)",
    lichess: "godofthecats",
    chesscom: "goodvibesplanet",
  },
  {
    name: "Aziz (SL)",
    chesscom: "stuffedlaugh",
  },
  {
    name: "Benji",
    lichess: "vegansandwich",
    chesscom: "nuubinator",
  },
];

const formatRatingWithDelta = (rating, delta) => {
  let color = "gray";
  if (delta > 0) color = "green";
  if (delta < 0) color = "red";
  return (
    <span>
      {rating}
      {typeof delta === "number" && delta !== 0 && (
        <span style={{ color, marginLeft: 4 }}>
          ({delta > 0 ? "+" : ""}
          {delta})
        </span>
      )}
      {typeof delta === "number" && delta === 0 && (
        <span style={{ color, marginLeft: 4 }}>(0)</span>
      )}
    </span>
  );
};

const BULLET_COLUMNS = [
  {
    label: "Name",
    renderCell: (item) => (
      <>
        {item.name}
        {item.champion && <Whatshot htmlColor="gold" />}
      </>
    ),
  },
  {
    label: "Bullet",
    renderCell: (item) =>
      formatRatingWithDelta(item.bulletRating, item.bulletDelta),
  },
  {
    label: "Games",
    renderCell: (item) => item.bulletGames,
  },
];

const BLITZ_COLUMNS = [
  {
    label: "Name",
    renderCell: (item) => (
      <>
        {item.name}
        {item.champion && <Whatshot htmlColor="gold" />}
      </>
    ),
  },
  {
    label: "Blitz",
    renderCell: (item) =>
      formatRatingWithDelta(item.blitzRating, item.blitzDelta),
  },
  {
    label: "Games",
    renderCell: (item) => item.blitzGames,
  },
];

const Ratings = () => {
  const [tableData, setTableData] = useState([]);
  const [lichessData, setLichessData] = useState([]);
  const [chesscomData, setChesscomData] = useState([]);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const lichessUsers = users.filter((u) => u.lichess);
    const chesscomUsers = users.filter((u) => u.chesscom);

    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, "0");

    // Fetch rating history
    const lichessRatingPromises = lichessUsers.map((user) =>
      axios.get(`https://lichess.org/api/user/${user.lichess}/rating-history`, {
        withCredentials: false,
      }),
    );
    // Fetch games for the current month
    const lichessGamesPromises = lichessUsers.map((user) =>
      axios
        .get(
          `https://lichess.org/api/games/user/${user.lichess}?since=${new Date(
            year,
            today.getMonth(),
            1,
          ).getTime()}&max=300&perfType=all&analysed=false`,
          {
            withCredentials: false,
            headers: { Accept: "application/x-ndjson" },
          },
        )
        .catch(() => ({ data: "" })),
    );

    const chesscomPromises = chesscomUsers.map((user) => {
      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, "0");

      const urls = [
        `https://api.chess.com/pub/player/${user.chesscom}/stats`,
        `https://api.chess.com/pub/player/${user.chesscom}/games/${year}/${month}`,
      ];

      return axios.all(
        urls.map((url) =>
          axios.get(url, { withCredentials: false }).catch((e) => e),
        ),
      );
    });

    Promise.all([
      Promise.all(lichessRatingPromises),
      Promise.all(lichessGamesPromises),
      Promise.all(chesscomPromises),
    ]).then(([lichessResponses, lichessGamesResponses, chesscomResponses]) => {
      // Lichess
      const lichessData = lichessResponses.map((response, index) => {
        const user = lichessUsers[index];
        const ratingHistory = response.data;
        const blitzPoints =
          ratingHistory.find((r) => r.name === "Blitz")?.points || [];
        const bulletPoints =
          ratingHistory.find((r) => r.name === "Bullet")?.points || [];

        const getDataFromPoints = (points) => {
          if (points.length === 0) {
            return { current: 0, change: 0 };
          }
          const today = new Date();
          const firstDay = startOfMonth(today);
          const filtered = points.filter(
            (point) => new Date(point[0], point[1], point[2]) < firstDay,
          );
          const current = points.length > 0 ? points[points.length - 1][3] : 0;
          const firstOfMonth =
            filtered.length > 0 ? filtered[filtered.length - 1][3] : current;

          return {
            current,
            change: current - firstOfMonth,
          };
        };

        // Count games in current month using games API
        let bulletGames = 0;
        let blitzGames = 0;
        const gamesNdjson = lichessGamesResponses[index].data;
        if (typeof gamesNdjson === "string" && gamesNdjson) {
          const lines = gamesNdjson.split("\n").filter(Boolean);
          for (const line of lines) {
            try {
              const game = JSON.parse(line);
              if (game.perf === "bullet") bulletGames++;
              if (game.perf === "blitz") blitzGames++;
            } catch (e) {}
          }
        }

        return {
          name: user.name,
          blitz: getDataFromPoints(blitzPoints),
          bullet: getDataFromPoints(bulletPoints),
          bulletGames,
          blitzGames,
        };
      });

      // Lichess
      const lichessBulletData = lichessData.map((item) => ({
        name: item.name,
        bulletRating: item.bullet?.current,
        bulletDelta: item.bullet?.change,
        bulletGames: item.bulletGames,
        champion: false,
      }));
      const lichessBlitzData = lichessData.map((item) => ({
        name: item.name,
        blitzRating: item.blitz?.current,
        blitzDelta: item.blitz?.change,
        blitzGames: item.blitzGames,
        champion: false,
      }));
      // Champion logic for Lichess
      const lichessBulletSorted = [...lichessBulletData].sort(
        (a, b) => (b.bulletRating || 0) - (a.bulletRating || 0),
      );
      if (lichessBulletSorted.length) {
        const champion = lichessBulletSorted.find((d) => d.bulletRating);
        if (champion) champion.champion = true;
      }
      const lichessBlitzSorted = [...lichessBlitzData].sort(
        (a, b) => (b.blitzRating || 0) - (a.blitzRating || 0),
      );
      if (lichessBlitzSorted.length) {
        const champion = lichessBlitzSorted.find((d) => d.blitzRating);
        if (champion) champion.champion = true;
      }

      // Chess.com
      const chesscomData = chesscomResponses.map((response, index) => {
        const user = chesscomUsers[index];
        const [stats, currentMonthArchives] = response;

        if (stats.status !== 200 || !stats.data.chess_blitz) {
          return { name: user.name };
        }

        const archives = [...(currentMonthArchives.data?.games || [])];
        const chesscomUsername = user.chesscom;
        const currentBlitz = stats.data.chess_blitz.last.rating;
        const currentBullet = stats.data.chess_bullet?.last?.rating || null;

        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth() + 1; // 1-based

        // Blitz delta
        const blitzGames = archives.filter((game) => {
          const gameDate = new Date(game.end_time * 1000);
          return (
            game.time_class === "blitz" &&
            gameDate.getFullYear() === year &&
            gameDate.getMonth() + 1 === month &&
            (game.white.username.toLowerCase() ===
              chesscomUsername.toLowerCase() ||
              game.black.username.toLowerCase() ===
                chesscomUsername.toLowerCase())
          );
        });
        const firstBlitzGameThisMonth = blitzGames.sort(
          (a, b) => a.end_time - b.end_time,
        )[0];

        let blitzAtStartOfMonth = currentBlitz;
        if (firstBlitzGameThisMonth) {
          if (
            firstBlitzGameThisMonth.white.username.toLowerCase() ===
            chesscomUsername.toLowerCase()
          ) {
            blitzAtStartOfMonth = firstBlitzGameThisMonth.white.rating;
          } else {
            blitzAtStartOfMonth = firstBlitzGameThisMonth.black.rating;
          }
        }

        // Bullet delta
        const bulletGames = archives.filter((game) => {
          const gameDate = new Date(game.end_time * 1000);
          return (
            game.time_class === "bullet" &&
            gameDate.getFullYear() === year &&
            gameDate.getMonth() + 1 === month &&
            (game.white.username.toLowerCase() ===
              chesscomUsername.toLowerCase() ||
              game.black.username.toLowerCase() ===
                chesscomUsername.toLowerCase())
          );
        });
        const firstBulletGameThisMonth = bulletGames.sort(
          (a, b) => a.end_time - b.end_time,
        )[0];

        let bulletAtStartOfMonth = currentBullet;
        if (firstBulletGameThisMonth && currentBullet !== null) {
          if (
            firstBulletGameThisMonth.white.username.toLowerCase() ===
            chesscomUsername.toLowerCase()
          ) {
            bulletAtStartOfMonth = firstBulletGameThisMonth.white.rating;
          } else {
            bulletAtStartOfMonth = firstBulletGameThisMonth.black.rating;
          }
        }

        return {
          name: user.name,
          chesscom: currentBlitz,
          chesscomChange: currentBlitz - blitzAtStartOfMonth,
          chesscomBullet: currentBullet,
          chesscomBulletChange:
            currentBullet !== null && bulletAtStartOfMonth !== null
              ? currentBullet - bulletAtStartOfMonth
              : null,
          chesscomBlitzGames: blitzGames.length,
          chesscomBulletGames: bulletGames.length,
        };
      });

      // Chess.com
      const chesscomBulletData = chesscomData.map((item) => ({
        name: item.name,
        bulletRating: item.chesscomBullet,
        bulletDelta: item.chesscomBulletChange,
        bulletGames: item.chesscomBulletGames,
        champion: false,
      }));
      const chesscomBlitzData = chesscomData.map((item) => ({
        name: item.name,
        blitzRating: item.chesscom,
        blitzDelta: item.chesscomChange,
        blitzGames: item.chesscomBlitzGames,
        champion: false,
      }));
      // Champion logic for Chess.com
      const chesscomBulletSorted = [...chesscomBulletData].sort(
        (a, b) => (b.bulletRating || 0) - (a.bulletRating || 0),
      );
      if (chesscomBulletSorted.length) {
        const champion = chesscomBulletSorted.find((d) => d.bulletRating);
        if (champion) champion.champion = true;
      }
      const chesscomBlitzSorted = [...chesscomBlitzData].sort(
        (a, b) => (b.blitzRating || 0) - (a.blitzRating || 0),
      );
      if (chesscomBlitzSorted.length) {
        const champion = chesscomBlitzSorted.find((d) => d.blitzRating);
        if (champion) champion.champion = true;
      }

      setLichessData({
        bullet: lichessBulletSorted,
        blitz: lichessBlitzSorted,
      });
      setChesscomData({
        bullet: chesscomBulletSorted,
        blitz: chesscomBlitzSorted,
      });
    });
  }, []);

  const materialTheme = getTheme(DEFAULT_OPTIONS);
  const theme = useTheme(materialTheme);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Chess.com" />
          <Tab label="Lichess" />
        </Tabs>
      </Box>
      <div>
        <Box
          sx={{
            marginTop: "1rem",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 4 },
            alignItems: "flex-start",
            maxWidth: 900,
            marginLeft: "auto",
            marginRight: "auto",
            padding: { xs: 1, sm: 2 },
          }}
        >
          {tab === 0 && (
            <>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  background: "#fff",
                  borderRadius: 2,
                  boxShadow: 2,
                  p: { xs: 1, sm: 2 },
                  mb: { xs: 2, sm: 0 },
                }}
              >
                <h3
                  style={{
                    margin: "8px 0 16px 0",
                    fontSize: 22,
                    textAlign: "center",
                    fontWeight: 700,
                    letterSpacing: 1,
                  }}
                >
                  Bullet
                </h3>
                <CompactTable
                  columns={BULLET_COLUMNS}
                  data={{ nodes: chesscomData.bullet || [] }}
                  theme={theme}
                  style={{ fontSize: 16 }}
                  rowProps={(row, idx) => ({
                    style: {
                      background: idx % 2 === 0 ? "#f7f7fa" : "#fff",
                      height: 44,
                    },
                  })}
                  cellProps={() => ({ style: { padding: "10px 8px" } })}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  background: "#fff",
                  borderRadius: 2,
                  boxShadow: 2,
                  p: { xs: 1, sm: 2 },
                }}
              >
                <h3
                  style={{
                    margin: "8px 0 16px 0",
                    fontSize: 22,
                    textAlign: "center",
                    fontWeight: 700,
                    letterSpacing: 1,
                  }}
                >
                  Blitz
                </h3>
                <CompactTable
                  columns={BLITZ_COLUMNS}
                  data={{ nodes: chesscomData.blitz || [] }}
                  theme={theme}
                  style={{ fontSize: 16 }}
                  rowProps={(row, idx) => ({
                    style: {
                      background: idx % 2 === 0 ? "#f7f7fa" : "#fff",
                      height: 44,
                    },
                  })}
                  cellProps={() => ({ style: { padding: "10px 8px" } })}
                />
              </Box>
            </>
          )}
          {tab === 1 && (
            <>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  background: "#fff",
                  borderRadius: 2,
                  boxShadow: 2,
                  p: { xs: 1, sm: 2 },
                  mb: { xs: 2, sm: 0 },
                }}
              >
                <h3
                  style={{
                    margin: "8px 0 16px 0",
                    fontSize: 22,
                    textAlign: "center",
                    fontWeight: 700,
                    letterSpacing: 1,
                  }}
                >
                  Bullet
                </h3>
                <CompactTable
                  columns={BULLET_COLUMNS}
                  data={{ nodes: lichessData.bullet || [] }}
                  theme={theme}
                  style={{ fontSize: 16 }}
                  rowProps={(row, idx) => ({
                    style: {
                      background: idx % 2 === 0 ? "#f7f7fa" : "#fff",
                      height: 44,
                    },
                  })}
                  cellProps={() => ({ style: { padding: "10px 8px" } })}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  background: "#fff",
                  borderRadius: 2,
                  boxShadow: 2,
                  p: { xs: 1, sm: 2 },
                }}
              >
                <h3
                  style={{
                    margin: "8px 0 16px 0",
                    fontSize: 22,
                    textAlign: "center",
                    fontWeight: 700,
                    letterSpacing: 1,
                  }}
                >
                  Blitz
                </h3>
                <CompactTable
                  columns={BLITZ_COLUMNS}
                  data={{ nodes: lichessData.blitz || [] }}
                  theme={theme}
                  style={{ fontSize: 16 }}
                  rowProps={(row, idx) => ({
                    style: {
                      background: idx % 2 === 0 ? "#f7f7fa" : "#fff",
                      height: 44,
                    },
                  })}
                  cellProps={() => ({ style: { padding: "10px 8px" } })}
                />
              </Box>
            </>
          )}
        </Box>
      </div>
    </>
  );
};
export default Ratings;
